import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { sendEvent } from "../../Utils/analytics";
import { getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import { useMediaQuery } from "../CustomHook/useMediaQuery";
import FormProvider from "../HookForm/FormProvider";
import { otpDigitTarget } from "./otpDigitTarget";

// ----------------------------------------------------------------------

type FormValuesProps = {
  code1: string;
  code2: string;
  code3: string;
  code4: string;
  code5: string;
  code6: string;
};

const ContentStyle = styled("div")(({ theme }) => ({
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  maxWidth: "480px",
  // CRL-3241: white rounded card on the grey page background (mobile dahil)
  background: "#FFFFFF",
  border: "1px solid #F2F4F5",
  boxShadow: "0px 2px 24px rgb(36 33 49 / 5%)",
  borderRadius: "24px",
  padding: "24px 20px",
  "@media (min-width: 768.5px)": {
    minHeight: "400px",
    padding: "40px",
  },
}));

// 131 -> "2:11" (mock shows the countdown as m:ss)
const formatExpireTime = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

type ValueNames = "code1" | "code2" | "code3" | "code4" | "code5" | "code6";

export default function OtpVerify() {
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthState();
  const isDesktop = useMediaQuery();
  const { token, verifyUser, loginUser, otpData, setOtpData } = useAuthState();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorState, setErrorState] = useState<boolean>(false);
  const [initialCounter, setInitialCounter] = useState(otpData?.expireIn || 180);
  const [expireTime, setExpireTime] = useState(otpData?.expireIn || 180);
  const { state }: any = useLocation();
  const phoneNumberFormatting = state?.phoneNumber?.replace(/^0|\D/g, "");
  const identityNoFormatting = state?.identityNo?.replace(/\D/g, "");
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const VerifyCodeSchema = Yup.object().shape({
    code1: Yup.string().required("Code is required"),
    code2: Yup.string().required("Code is required"),
    code3: Yup.string().required("Code is required"),
    code4: Yup.string().required("Code is required"),
    code5: Yup.string().required("Code is required"),
    code6: Yup.string().required("Code is required"),
  });

  const defaultValues = {
    code1: "",
    code2: "",
    code3: "",
    code4: "",
    code5: "",
    code6: "",
  };

  const methodsSubmit = useForm({
    mode: "all",
    resolver: yupResolver(VerifyCodeSchema),
    defaultValues,
  });

  const {
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methodsSubmit;

  const values = watch();

  useEffect(() => {
    const interval = setInterval(() => {
      setExpireTime((prev: any) => {
        if (prev > 0) return prev - 1;
        else {
          clearInterval(interval);
          return prev;
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!state) {
      navigate("/");
    } else if (isLoggedIn()) {
      navigate("/payments");
    } else {
      // Only call otpTokenSend if there's no existing otpData in context
      if (!otpData) {
        otpTokenSend();
        setOtpData(undefined);
      }
      if (expireTime > 0) {
        const handleVisibilityChange = () => {
          if (document.visibilityState === "visible") {
            // Also check for otpData from context here to avoid unnecessary calls on visibility change
            if (!otpData) {
              otpTokenSend();
            }
          }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
        };
      }
    }
    document.addEventListener("paste", handlePasteClipboard);
  }, []);

  const onSubmit = async (data: FormValuesProps) => {
    const otpCode = Object.values(data).join("");
    if (token) {
      const route = await verifyUser(otpCode, token);
      if (route) {
        navigate(route, { replace: true });
      } else {
        setErrorState(true);
        setHasSubmitted(true);
      }
      sendEvent(`click_validate_otp_button`, {});
    }
  };

  const handlePasteClipboard = (event: ClipboardEvent) => {
    let data: string | string[] = event?.clipboardData?.getData("Text") || "";

    data = data.split("");

    [].forEach.call(
      document.querySelectorAll("#field-code"),
      (node: any, index) => {
        node.value = data[index];
        const fieldIndex = `code${index + 1}`;
        setValue(fieldIndex as ValueNames, data[index]);
      }
    );
  };

  const focusOtpField = (index: number) => {
    const field = document.querySelector(
      `input[name=code${index}]`
    ) as HTMLInputElement | null;
    field?.focus();
  };

  /**
   * Two cases the change handler can't cover:
   * - Backspace on an empty box fires no change event → clear the previous
   *   box and move focus to it (chained deletes work).
   * - A digit typed into a full box is blocked by maxLength → the user has
   *   backspaced back onto a filled box and kept typing, so the digit goes
   *   into the next box (see otpDigitTarget) instead of replacing this one.
   */
  const handleOtpKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLInputElement;
    const fieldIntIndex = Number(target.name.replace("code", ""));
    if (!fieldIntIndex) return;

    if (e.key === "Backspace" && target.value.length === 0 && fieldIntIndex > 1) {
      e.preventDefault();
      setValue(`code${fieldIntIndex - 1}` as ValueNames, "", {
        shouldValidate: true,
      });
      focusOtpField(fieldIntIndex - 1);
      return;
    }

    if (/^[0-9]$/.test(e.key) && target.value.length >= 1) {
      e.preventDefault();
      const { writeTo, focusTo, submit } = otpDigitTarget(fieldIntIndex);
      setValue(`code${writeTo}` as ValueNames, e.key, { shouldValidate: true });
      focusOtpField(focusTo);
      if (submit && !hasSubmitted) {
        handleSubmit(onSubmit)();
      }
    }
  };

  const handleChangeWithNextField = (
    event: React.ChangeEvent<HTMLInputElement>,
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  ) => {
    const { maxLength, value, name } = event.target;
    const fieldIndex = name.replace("code", "");
    const fieldIntIndex = Number(fieldIndex);
    handleChange(event);

    // Eğer değer girildiyse sonraki alana geç
    if (value.length >= maxLength) {
      if (fieldIntIndex < 6) {
        const nextField = document.querySelector(
          `input[name=code${fieldIntIndex + 1}]`
        );
        (nextField as HTMLElement).focus();
      } else if (!hasSubmitted) {
        // Son alana değer girildiyse ve daha önce submit edilmediyse formu submit et
        handleSubmit(onSubmit)();
      }
    }
    if (value.length <= 0 && fieldIntIndex != 1) {
      const nextfield = document.querySelector(
        `input[name=code${fieldIntIndex - 1}]`
      );
      (nextfield as HTMLElement).focus();
    }
    handleChange(event);
  };

  const otpTokenSend = async () => {
    if (phoneNumberFormatting) {
      const resData = await loginUser(phoneNumberFormatting, identityNoFormatting);
      if (resData) {
        setInitialCounter(resData.data.expireIn);
        setExpireTime(resData.data.expireIn);
      }
    }
  };

  const againSendOtp = async () => {
    setLoading(true);
    await otpTokenSend();
    setLoading(false);
    sendEvent(`click_send_otp_again_button`, {});
    const interval = setInterval(() => {
      setExpireTime((prev: any) => {
        if (prev > 0) return prev - 1;
        else {
          clearInterval(interval);
          return prev;
        }
      });
    }, 1000);
  };

  const handleKeyPress = (e: any) => {
    // Sadece rakam girişine izin verme
    if (e.key === " " || isNaN(Number(e.key))) {
      e.preventDefault();
    }

    if (e.key === "Enter") {
      handleSubmit(onSubmit)();
    }
  };
  return (
    <Box>
      <ContentStyle>
        <Stack direction="column" alignItems="start" gap={2} sx={{ mb: 2 }}>
          {isDesktop && (
            <Link
              to={"/"}
              onClick={() => {
                sendEvent(`click_back_button_otp_page`, {});
              }}
            >
              <ArrowBackIosIcon fontSize="small" sx={{ color: "#191B1E" }} />
            </Link>
          )}
          <Box sx={{ flexGrow: 1 }} display={"flex"} alignItems={"center"}>
            {!isDesktop && (
              <Link
                to={"/"}
                style={{ lineHeight: 1 }}
                onClick={() => {
                  sendEvent(`click_back_button_otp_page`, {});
                }}
              >
                <ArrowBackIosIcon
                  fontSize="small"
                  sx={{ color: "#191B1E", mr: 1 }}
                />
              </Link>
            )}
            <Typography
              fontSize={"20px"}
              fontWeight={"700"}
              lineHeight={1.25}
              sx={{
                "@media (min-width: 768.5px)": {
                  fontSize: "24px",
                },
              }}
            >
              {getRemoteValue("otp_verify_header")}
            </Typography>
          </Box>
        </Stack>
        <Typography
          mb={3}
          fontSize={"16px"}
          fontWeight={400}
          sx={{
            color: "text.secondary",
            "@media (min-width: 768.5px)": {
              fontSize: "14px",
            },
          }}
        >
          {getRemoteValue("otp_verify_desc1")}
          <Typography fontWeight={600} color={"black"} display={"contents"}>
            {" "}
            0
            {phoneNumberFormatting &&
              phoneNumberFormatting.replace(
                /^(\d{3})\d{5}(\d{2})$/,
                "$1*****$2"
              )}{" "}
          </Typography>
          {getRemoteValue("otp_verify_desc2")}
        </Typography>
        <FormProvider methods={methodsSubmit} onSubmit={handleSubmit(onSubmit)}>
          <Stack direction="row" spacing={2} justifyContent="space-around">
            {Object.keys(values).map((name, index) => (
              <Controller
                key={name}
                name={`code${index + 1}` as ValueNames}
                control={control}
                render={({ field }) => (
                  <OutlinedInput
                    {...field}
                    id="field-code"
                    autoFocus={index === 0}
                    placeholder="-"
                    type="tel"
                    inputMode="tel"
                    disabled={expireTime == 0}
                    sx={
                      errorState
                        ? {
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "2px solid #EF4444", // Doluysa mavi
                            borderRadius: "8px",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: field.value ? "#EF4444" : "#C0CCDA", // Doluysa mavi, boşsa gri
                          },
                        }
                        : {
                          backgroundColor: "#FBFBFD",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: field.value
                              ? "2px solid #191B1E"
                              : "1px solid #E2E8F0",

                            // Doluysa siyah, boşsa gri
                            borderRadius: "12px",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: field.value ? "#191B1E" : "#CBD5E1", // Doluysa siyah, boşsa gri
                          },
                        }
                    }
                    onFocus={() => setErrorState(false)}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeWithNextField(event, field.onChange)
                    }
                    onKeyDown={handleOtpKeyDown}
                    onKeyPress={(e) => hasSubmitted && handleKeyPress(e)}
                    inputProps={{
                      maxLength: 1,
                      sx: {
                        p: 0,
                        textAlign: "center",
                        width: 40,
                        height: 50,
                        "@media (min-width: 420px)": {
                          width: 45,
                          height: 55,
                        },

                        "@media (min-width: 768.5px)": {
                          width: 50,
                          height: 60,
                        },
                        color: `${errorState ? "#EF4444" : "#191B1E"} `,
                        fontSize: "24px",
                        fontWeight: 700,
                      },
                    }}
                  />
                )}
              />
            ))}
          </Stack>
          <Stack mb={2} mt={4}>
            {!loading ? (
              <Stack
                display={"flex"}
                flexDirection={"row"}
                alignItems={"center"}
              >
                <LinearProgress
                  sx={{
                    height: "6px",
                    borderRadius: "8px",
                    width: "100%",
                    mr: 2,
                    backgroundColor: "#E2E8F0",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#191B1E",
                      borderRadius: "8px",
                    },
                  }}
                  variant="determinate"
                  value={(expireTime / 180) * 100}
                  valueBuffer={(expireTime / initialCounter) * 100}
                />
                <Stack fontWeight={600} fontSize={"16px"} color={"#191B1E"}>
                  {formatExpireTime(expireTime)}
                </Stack>
              </Stack>
            ) : (
              <Grid textAlign="center">
                <CircularProgress />
              </Grid>
            )}

            {expireTime == 0 && (
              <Typography
                style={{
                  marginTop: "16px",
                  color: "#e00000",
                  textAlign: "center",
                  fontSize: " 13px",
                  fontWeight: "600",
                }}
              >
                {getRemoteValue("otp_timer_warning")}
              </Typography>
            )}
          </Stack>
          <CLoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!isValid}
            sx={{ mt: 3 }}
          >
            {getRemoteValue("approve")}
          </CLoadingButton>
          <Stack
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"center"}
            alignItems={"center"}
            mt={2}
          >
            <Typography
              sx={{ textAlignLast: "end" }}
              width={"100%"}
              color={"#475569"}
              fontSize={"14px"}
              fontWeight={400}
              mr={2}
            >
              {getRemoteValue("did_not_receive_code")}
            </Typography>
            <Button
              className="dark:hover:red"
              sx={{
                justifyContent: "start",
                color: "#191B1E",
                fontSize: "16px",
                fontWeight: 500,
                letterSpacing: "0",
                textTransform: "initial",
                lineHeight: 1,
                "&:hover": {
                  backgroundColor: "transparent", // Hover efektini kaldırmak için arka planı şeffaf yapabilirsiniz
                },
              }}
              fullWidth
              disabled={expireTime > 0 || loading}
              onClick={() => {
                againSendOtp();
              }}
              variant="text"
            >
              {getRemoteValue("again_send_text")}
            </Button>
          </Stack>
        </FormProvider>
      </ContentStyle>
    </Box>
  );
}

// function convertSeconds(seconds: number) {
//   return new Date(seconds * 1000).toISOString().slice(14, 19);
// }
